import {
  Button,
  ButtonLink,
  Input,
  Modal,
  InputTypeSelectAddress,
  generateRandomId,
  getCountryByLabel,
} from '@rabbit/elements/shared-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DTConsumer_Private } from '@rabbit/data/types';
import { getFirstName, getLastName, useOliveAPI } from '@rabbit/bizproc/react';
import {
  DR_Consumer_Private,
  PortalEditingDocumentStatus,
  usePortalDocumentEditor,
} from '@rabbit/data/portal';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useContext,
  useState,
  useRef,
} from 'react';
import { NavLink, useNavigate, Navigate } from 'react-router-dom';
import { DRDocType, firebaseAuth } from '@rabbit/firebase/react';
import ROUTE_NAME from '../../utils/url-constants';
import { t } from 'i18next';
import { updateProfile } from 'firebase/auth';
import OnboardingView from '@rabbit/olive/views/OnboardingView';
import { AppContext } from '@rabbit/app-context';
import { LIST_COUNTRIES } from '@rabbit/bizproc/react';
import { SelectAddressShape } from '@rabbit/elements/shared-types';
import { Address } from '@rabbit/data/types';
import { toast } from 'react-toastify';
import { AGE_RANGE_OPTIONS } from '@rabbit/olive/utils/consts';
import { ConditionalProfileFields } from '../../components/molecules/ConditionalProfileFields/ConditionalProfileFields';

interface CompleteProfileFormValuesShape {
  first_name: string;
  last_name: string;
  dob?: Date | null;
  email: string;
  opt_out_marketing: boolean;
  isCommercialUser?: string;
  phone?: string;
  phone_e164?: string;
  age_bracket?: string;
  consumer_address?: Address | null;
}

export interface FormCompleteProfileProps {
  id: string;
  data: DTConsumer_Private;
  authProvider: string | undefined;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export function FormCompleteProfile({
  id,
  data,
  authProvider,
  setIsLoading,
}: FormCompleteProfileProps) {
  const tenantLink = t('tenantLink');
  const { config, tenantInfo } = useContext(AppContext);
  const { commercialUseStatusUpdateWarranties } = useOliveAPI();
  const commercialUsersEnabled =
    t('CFG_COMMERCIAL_CONSUMER_USERS_ENABLED') === 'true';
  const placeholderText =
    t(
      'CFG_MARKETING_SIGNUP_INFO_PLACEHOLDER',
      `${
        tenantInfo?.name?.toLowerCase() === 'supagard'
          ? 'Yes, subscribe me to your emails.'
          : "Please don't send me promotional emails."
      }`
    ) || t('general.pleaseDontSendMePromotionalEmails');
  const phoneNumberRequired =
    config.CUSTOMER.COMPLETE_PROFILE.PHONE_NUMBER_REQUIRED;
  const ageBracketRequired = config.CUSTOMER.COMPLETE_PROFILE.AGE_BRACKET_REQUIRED;
  const addressRequired = config.CUSTOMER.COMPLETE_PROFILE.ADDRESS_REQUIRED;
  const searchParams = new URLSearchParams(window.location.search);
  const brand = searchParams.get('brand');

  const schema = Yup.object({
    first_name: Yup.string()
      .required('Please enter a first name')
      .matches(
        /^(?:[A-Z][a-z]*['´`-]?\s?){1,2}|^[A-Z][a-z]*$/,
        'First name must start with a capital letter and contain only letters.'
      ),
    last_name: Yup.string()
      .trim()
      .required('Please enter a last name')
      .matches(
        /^(?:[A-Z][a-z]*['´`-]?\s?){1,2}|^[A-Z][a-z]*$/,
        'Last name must start with a capital letter and contain only letters.'
      ),
    ...(phoneNumberRequired && {
      phone: Yup.string().required('Please enter a phone number'),
    }),
    ...(ageBracketRequired && {
      age_bracket: Yup.string().required('Please select your age range'),
    }),
    // dob: Yup.date()
    //   //.required('Date of birth is required')
    //   .max(new Date(), 'Invalid date'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Please enter an email address'),

    ...(commercialUsersEnabled && {
      isCommercialUser: Yup.boolean().required('Please fill in the field'),
    }),
  });

  const { body, update, commit, isReady, status } = usePortalDocumentEditor(
    DR_Consumer_Private as DRDocType<DTConsumer_Private>,
    id
  );
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressButtonLoading, setAddressButtonLoading] = useState(false);
  const addressFormikRef = useRef(null) as any;
  const [selectedAddress, setSelectedAddress] =
    useState<SelectAddressShape | null>({} as SelectAddressShape);
  const mainFormikRef = useRef(null) as any;
  const hasNavigatedAwayRef = useRef(false);

  const initialValues: CompleteProfileFormValuesShape = {
    first_name:
      data.fullname && data.fullname.indexOf('@') === -1
        ? getFirstName(data.fullname)
        : '',
    last_name: data.fullname ? getLastName(data.fullname) : '',
    email: data?.email ? data?.email : '',
    opt_out_marketing: false,
    ...(phoneNumberRequired && {
      phone: data ? data.phone : '',
      phone_e164: data ? data.phone : '',
    }),
    ...(ageBracketRequired && {
      age_bracket: data?.age_bracket ?? '',
    }),
    consumer_address: null,
  };

  // Address form schema and helpers (copied/adapted from AccountAddressForm)
  const addressSchema = Yup.object({
    postcode: Yup.string().trim().required('Please enter a post code'),
    line1: Yup.string().trim().required('Please enter an address'),
    line2: Yup.string().trim(),
    town: Yup.string().trim().required('Please enter a town'),
    state: Yup.string().trim().required('Please enter a state'),
    country: Yup.string().trim().required('Please enter a country'),
  });

  const addressInitialValues = {
    postcode: '',
    line1: '',
    line2: '',
    town: '',
    state: '',
    country: tenantInfo?.country ?? '',
    // Keep optional fields to align with AccountAddressForm UX
    label: '',
    isDefault: true,
  };

  const setLoadingState = (state: boolean) => {
    setLoading(state);
    setIsLoading(state);
  };

  // Helpers to update address array
  const updateDefaultAddress = () => {
    if (body && Array.isArray(body?.address)) {
      const updatedData = body?.address ? [...body.address] : [];
      return updatedData.map((addr) => ({ ...addr, isDefault: false }));
    }
  };

  const addOrEditAddress = (currentAddress: any) => {
    if (body?.address && Array.isArray(body?.address)) {
      const existingIndex = body?.address?.findIndex(
        (obj) => obj.id === currentAddress.id
      );
      let updatedData = body?.address ? [...body.address] : [];
      if (existingIndex !== -1) {
        if (currentAddress.isDefault)
          updatedData = updateDefaultAddress() ?? [];
        updatedData[existingIndex] = currentAddress;
        return updatedData;
      } else {
        if (currentAddress.isDefault)
          updatedData = updateDefaultAddress() ?? [];
        return [...updatedData, currentAddress];
      }
    } else {
      return [currentAddress];
    }
  };

  const saveAddress = async (address: Address) => {
    if (!body || !isReady) return;
    try {
      update({
        ...body,
        address: addOrEditAddress(address),
      });
      await commit();
      toast.success('Address saved successfully!');
      // Update main form preview + validation field
      try {
        mainFormikRef.current?.setFieldValue('consumer_address', address, true);
      } catch (e) {
        console.error('Failed to set consumer_address on main form', e);
      }
      setAddressModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onAddressSubmit = async (values: any) => {
    setAddressButtonLoading(true);
    setIsLoading(true);
    try {
      const addressInfo = {
        id: generateRandomId(),
        line1: values.line1,
        line2: values.line2,
        company: '',
        town: values.town,
        postcode: values.postcode,
        county: values.state,
        country: values.country,
      } as Address;
      await saveAddress(addressInfo);
    } catch (error) {
      console.log(error);
    }
    setAddressButtonLoading(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!selectedAddress) {
      addressFormikRef.current?.resetForm?.();
      return;
    }
    if (addressFormikRef?.current && selectedAddress?.postal_code) {
      addressFormikRef.current.setFieldValue('line1', selectedAddress.line_1);
      addressFormikRef.current.setFieldValue('line2', selectedAddress.line_2);
      addressFormikRef.current.setFieldValue('town', selectedAddress.locality);
      addressFormikRef.current.setFieldValue(
        'state',
        selectedAddress.province_name
      );
      addressFormikRef.current.setFieldValue(
        'postcode',
        selectedAddress.postal_code
      );
      addressFormikRef.current.setFieldValue(
        'country',
        getCountryByLabel(selectedAddress.country_name)?.value ?? ''
      );
    }
  }, [selectedAddress]);

  // If there is already an address in the profile, prefill preview field
  useEffect(() => {
    if (!isReady) return;
    const defaultAddress = Array.isArray(body?.address)
      ? body?.address.find((a) => a.isDefault) || body?.address[0]
      : null;
    if (defaultAddress) {
      try {
        mainFormikRef.current?.setFieldValue(
          'consumer_address',
          defaultAddress,
          false
        );
      } catch (e) {
        console.error('Failed to prefill consumer_address on main form', e);
      }
    }
  }, [isReady, body?.address]);

  const saveProfile = async (user: any) => {
    if (!body || !isReady) return;
    setLoadingState(true);
    try {
      update({
        ...body,
        fullname: user.fullname,
        splitname: user.splitname,
        //dob: user.dob,
        marketing: user.marketing,
        phone: user.phone,
        age_bracket: user.age_bracket,
        acceptedTerms: Math.floor(new Date().getTime()),
        isOnboarded: true,
        ...(typeof user.isCommercialUser === 'boolean' && {
          isCommercialUser: user.isCommercialUser,
        }),
      });
      await commit();
      if (typeof user.isCommercialUser === 'boolean') {
        console.log('Checking holdings and generating warranties');
        await commercialUseStatusUpdateWarranties({
          consumerLink: id,
          premiumTenantLink: tenantLink,
        });
      }
      setLoadingState(false);
    } catch (error) {
      console.log('error', error);
      setLoadingState(false);
    }
  };

  const onSubmit = async (values: CompleteProfileFormValuesShape) => {
    try {
      const data = {
        fullname: values.first_name + ' ' + values.last_name,
        splitname: {
          first: values.first_name,
          last: values.last_name,
        },
        ...(phoneNumberRequired && {
          phone: values.phone_e164 ?? values.phone,
        }),
        ...(ageBracketRequired && {
          age_bracket: values.age_bracket,
        }),
        marketing: values.opt_out_marketing
          ? !!config.CUSTOMER.MARKETING_SIGNUP_INFO.OPT_OUT_MARKETING
          : !config.CUSTOMER.MARKETING_SIGNUP_INFO.OPT_OUT_MARKETING,
        ...(values.isCommercialUser && {
          isCommercialUser: values.isCommercialUser === 'true' ? true : false,
        }),
      };
      updateProfile(firebaseAuth.currentUser!, {
        displayName: data.fullname,
      });
      await saveProfile(data);

      if (body?.isOnboarded === false) {
        navigate(ROUTE_NAME.ONBOARDING);
      } else {
        navigate(ROUTE_NAME.DASHBOARD);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    // Auto-submit for Google/Apple when name is pre-filled and no extra fields required.
    // onSubmit already navigates (ONBOARDING or DASHBOARD); do not navigate again here.
    if (
      body &&
      isReady &&
      (authProvider === 'Google' || authProvider === 'Apple') &&
      !phoneNumberRequired &&
      !ageBracketRequired &&
      !body?.splitname?.first &&
      !body?.splitname?.last &&
      initialValues?.first_name &&
      initialValues?.last_name
    ) {
      (async () => {
        await onSubmit(initialValues);
      })().catch((err) => console.log(err));
      return;
    }

    // Already saved (e.g. user revisited complete-profile): sync Firebase displayName
    // so PrivateRoutes won't redirect back here, then go to dashboard (avoids redirect loop).
    if (
      !hasNavigatedAwayRef.current &&
      status === PortalEditingDocumentStatus.saved &&
      isReady &&
      !loading &&
      body?.splitname?.first &&
      body?.splitname?.last
    ) {
      hasNavigatedAwayRef.current = true;
      const fullName = [body.splitname.first, body.splitname.last]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (firebaseAuth.currentUser && fullName) {
        updateProfile(firebaseAuth.currentUser, { displayName: fullName })
          .then(() => navigate(ROUTE_NAME.DASHBOARD))
          .catch((err) => {
            console.error('Failed to sync displayName before redirect', err);
            hasNavigatedAwayRef.current = false;
          });
      } else {
        navigate(ROUTE_NAME.DASHBOARD);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    body,
    body?.splitname,
    body?.splitname?.first,
    body?.splitname?.last,
    isReady,
    authProvider,
    status,
    loading,
  ]);

  return (
    <div className="flex h-screen flex-col overflow-y-scroll md:h-auto md:overflow-y-auto">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={schema.concat(
          Yup.object({
            ...(addressRequired && {
              consumer_address: Yup.object()
                .nullable()
                .required('Please add an address'),
            }),
          })
        )}
        onSubmit={onSubmit}
        innerRef={mainFormikRef}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form className="flex flex-col gap-3">
            <div>
              <Input
                type="text"
                name="first_name"
                settings={{
                  id: 'first_name',
                  placeholder: 'First name',
                  hint: '*required',
                  forceUpperCaseFirstLetter: true,
                }}
              />
            </div>
            <div>
              <Input
                type="text"
                name="last_name"
                settings={{
                  id: 'last_name',
                  placeholder: 'Last name',
                  hint: '*required',
                  forceUpperCaseFirstLetter: true,
                }}
              />
            </div>
            {/* <div>
              <Input
                type="datepicker"
                name="dob"
                settings={{
                  id: 'dob',
                  maxDate: subtractYears(new Date(), 18),
                  initialDate: subtractYears(new Date(), 18),
                  placeholder: 'Date of birth',
                }}
              />
            </div> */}
            <div>
              <Input
                type="email"
                name="email"
                settings={{
                  id: 'email',
                  disabled: true,
                  hint: '*required',
                }}
              />
            </div>
            <ConditionalProfileFields
              showPhone={phoneNumberRequired}
              showAgeRange={ageBracketRequired}
            />

            {addressRequired && (
              <div className="mt-4">
                <ButtonLink
                  label={
                    (addressRequired
                      ? t('Residential address')
                      : t('general.address')) + (addressRequired ? '*' : '')
                  }
                  className={
                    errors?.consumer_address ? 'border-red-500 bg-red-100' : ''
                  }
                  onClick={() => setAddressModalOpen(true)}
                >
                  {values?.consumer_address ? (
                    <div>
                      {values.consumer_address?.postcode}{' '}
                      {values.consumer_address?.line1},{' '}
                      {values.consumer_address?.line2}{' '}
                      {values.consumer_address?.town}{' '}
                      {values.consumer_address?.county}{' '}
                      {values.consumer_address?.country}
                    </div>
                  ) : (
                    <div>
                      {addressRequired
                        ? t('Residential address')
                        : t('general.address')}
                    </div>
                  )}
                </ButtonLink>
              </div>
            )}
            {commercialUsersEnabled && (
              <div>
                <p className="font-nunito pb-2 font-medium">
                  Will you be using your products for commercial purposes?{' '}
                </p>
                <Input
                  type="radio"
                  name="isCommercialUser"
                  settings={{
                    values,
                    // Formik didn't like me using a boolean here, so this is a string so I don't waste too much time here - DC
                    radioValue: 'true',
                    radioLabel: 'Yes',
                    radioLabelStyles: 'font-nunito font-medium text-gray-500',
                  }}
                />
                <Input
                  type="radio"
                  name="isCommercialUser"
                  settings={{
                    values,
                    radioValue: 'false',
                    radioLabel: 'No',
                    radioLabelStyles: 'font-nunito font-medium text-gray-500',
                  }}
                />
              </div>
            )}
            <div>
              <p className="font-nunito font-medium">
                {`${
                  (config.VENDABLES.SHOW_ONLY_BRAND_PRODUCTS &&
                    brand &&
                    brand
                      .split('-')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join('-')) ||
                  (config.CUSTOMER.MARKETING_SIGNUP_INFO.SHOW_TENANT_NAME &&
                    tenantInfo?.name) ||
                  'iWarranty'
                } will occasionally send you promotional emails. You can opt out at any time in your account settings or directly from the marketing email.`}
              </p>
            </div>
            <div className="font-nunito">
              <Input
                type="checkbox"
                name="opt_out_marketing"
                settings={{
                  checkboxLabel: placeholderText,
                }}
              />
            </div>

            <div className="font-nunito fixed bottom-0 left-0 min-w-full rounded-tl-[30px] rounded-tr-[30px] bg-gray-100 font-medium md:relative md:bg-transparent">
              <div className="px-10 py-5 md:px-0 md:py-0">
                <div className="mt-4 flex">
                  <Button
                    kind="primary"
                    type="submit"
                    disabled={Object.keys(errors).length > 0}
                    loading={loading}
                  >
                    {authProvider ? `Sign up with ${authProvider}` : 'Sign up'}
                  </Button>
                </div>
                <div className="mt-4 text-left">
                  By continuing, you agree to:
                  <ol className="list-inside list-decimal">
                    {config?.LEGAL?.PRIVACY_POLICY_URL && (
                      <li>
                        <a
                          href={config.LEGAL.PRIVACY_POLICY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {tenantInfo?.name}'s Privacy Policy
                        </a>
                      </li>
                    )}
                    <li>
                      <NavLink
                        to={ROUTE_NAME.TERMS_AND_CONDITIONS}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        iWarranty's Terms & Conditions
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to={ROUTE_NAME.PRIVACY_POLICY}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        iWarranty's Privacy Policy
                      </NavLink>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {/* Address Modal */}
      {addressModalOpen && (
        <Modal
          kind="generic"
          className="m-5 max-w-[512px]"
          settings={{
            title: 'Add address',
            handleClose: () => setAddressModalOpen(false),
          }}
        >
          <Formik
            initialValues={addressInitialValues}
            validateOnChange={false}
            onSubmit={onAddressSubmit}
            validationSchema={addressSchema}
            innerRef={addressFormikRef}
          >
            {(props) => (
              <Form>
                <div className="mt-1 flex flex-col gap-4 p-5 pl-5 pr-5">
                  <Input
                    type="select"
                    label="Country"
                    name="country"
                    settings={{
                      options: LIST_COUNTRIES,
                      hint: '*required',
                    }}
                  />
                  <div>
                    <InputTypeSelectAddress
                      errors={null}
                      onChange={(address) => setSelectedAddress(address)}
                      country={props.values.country}
                    />
                  </div>
                  <Input
                    type="text"
                    label="Address line"
                    name="line1"
                    settings={{
                      hint: '*required',
                    }}
                  />
                  <Input
                    type="text"
                    label="Address line 2"
                    name="line2"
                    settings={{}}
                  />
                  <Input
                    type="text"
                    label="City/Suburb"
                    name="town"
                    settings={{
                      hint: '*required',
                    }}
                  />
                  <Input
                    type="text"
                    label="State/Province"
                    name="state"
                    settings={{
                      hint: '*required',
                    }}
                  />
                  <Input
                    type="text"
                    label={t('general.postCode')}
                    name="postcode"
                    settings={{
                      id: 'postcode',
                      hint: '*required',
                      placeholder: t('general.postCode'),
                    }}
                  />
                </div>
                <div className="mt-4 ml-5 mr-5 flex gap-3">
                  <Button
                    kind="outline"
                    type="button"
                    onClick={() => setAddressModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    kind="primary"
                    type="submit"
                    loading={addressButtonLoading}
                  >
                    Save address
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
    </div>
  );
}

export default FormCompleteProfile;
